import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { getAuthToken } from '../lib/api';
import { useUser } from '../context/UserContext';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
};

const initials = name => (name || '').split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

// Caret SVG for collapsed/expanded folder rows.
const Caret = ({ expanded }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" style={{ flexShrink: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
    <path d="M3 1 L7 5 L3 9" stroke={B.textSecondary} strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FolderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
    <path d="M1.5 4.5h4l1.5 1.5h7.5v7.5h-13z" stroke={B.gold} strokeWidth="1" fill="none"/>
  </svg>
);

const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
    <path d="M3 1.5h7l3 3v10h-10z" stroke={B.purple} strokeWidth="1" fill="none"/>
    <path d="M10 1.5v3h3" stroke={B.purple} strokeWidth="1" fill="none"/>
  </svg>
);

function TreeNode({ item, depth }) {
  const [expanded, setExpanded] = useState(depth === 0); // top-level folders open by default
  const indent = depth * 18;

  if (item.type === 'folder') {
    // Show the most accurate count we have: children we walked, otherwise
    // childCount from Graph (preserved at MAX_DEPTH so the UI doesn't say
    // "0 items" for a folder that just wasn't traversed).
    const count = Array.isArray(item.children) ? item.children.length : (item.childCount ?? 0);
    return (
      <div>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(e => !e)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `7px 12px 7px ${12 + indent}px`, cursor: 'pointer', userSelect: 'none', borderRadius: 4, width: '100%', background: 'transparent', border: 'none', textAlign: 'left', font: 'inherit', color: 'inherit' }}
          onMouseEnter={e => e.currentTarget.style.background = B.surface}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <Caret expanded={expanded} />
          <FolderIcon />
          <span style={{ fontSize: 13, color: B.textPrimary, fontWeight: 500 }}>{item.name}</span>
          <span style={{ fontSize: 11, color: B.textMuted, marginLeft: 6 }}>{count} item{count === 1 ? '' : 's'}{item.truncated ? ' · truncated' : ''}</span>
        </button>
        {expanded && item.children?.map(child => (
          <TreeNode key={child.id} item={child} depth={depth + 1} />
        ))}
      </div>
    );
  }

  // File
  return (
    <a
      href={item.webUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `7px 12px 7px ${30 + indent}px`, color: B.textSecondary, textDecoration: 'none', fontSize: 13, borderRadius: 4 }}
      onMouseEnter={e => { e.currentTarget.style.background = B.surface; e.currentTarget.style.color = B.textPrimary; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = B.textSecondary; }}>
      <FileIcon />
      <span>{item.name}</span>
    </a>
  );
}

// One bucket section — collapsible header + tree (or inline error). The
// first bucket starts expanded so the page isn't empty on load; the rest
// collapse so three deep trees don't dump on the user at once.
function BucketSection({ bucket, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const items = bucket.items || [];
  // Stable region id from name+path so duplicate names still get unique aria.
  const regionId = `bucket-${bucket.name}-${bucket.path}`.replace(/[^a-zA-Z0-9_-]/g, '-');

  return (
    <section style={{ marginBottom: 18 }}>
      {/* h2 wraps the button so the heading semantics aren't nested inside
          interactive content (screen readers prefer heading > button). */}
      <h2 style={{ margin: 0 }}>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={regionId}
          onClick={() => setExpanded(e => !e)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', color: 'inherit', font: 'inherit', fontSize: 14, fontWeight: 600, letterSpacing: '0.02em' }}>
          <Caret expanded={expanded} />
          <span style={{ color: B.textPrimary }}>{bucket.name}</span>
          {!bucket.error && (
            <span style={{ fontSize: 11, color: B.textMuted, fontWeight: 400, letterSpacing: 0 }}>{items.length} item{items.length === 1 ? '' : 's'}</span>
          )}
        </button>
      </h2>
      {/* Render the region wrapper unconditionally so aria-controls always
          resolves to a real element; toggle visibility via `hidden`. Tree
          contents stay conditional so collapsed sections don't pay the
          render cost for large trees. */}
      <div
        id={regionId}
        hidden={!expanded}
        style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 10, padding: '6px', minHeight: 60 }}
      >
        {expanded && (
          bucket.error ? (
            <p style={{ padding: 18, color: '#c0392b', fontSize: 13, textAlign: 'center' }}>
              Couldn’t load {bucket.name} — please try again.
            </p>
          ) : items.length === 0 ? (
            <p style={{ padding: 18, color: B.textSecondary, fontSize: 13, textAlign: 'center' }}>
              No documents in this section.
            </p>
          ) : (
            items.map(item => <TreeNode key={item.id} item={item} depth={0} />)
          )
        )}
      </div>
    </section>
  );
}

export default function PolicyDocs() {
  const navigate = useNavigate();
  const { employee } = useUser();
  const { oktaAuth } = useOktaAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const token = getAuthToken();
        // X-Auth-Token, not Authorization: SWA's proxy replaces the
        // Authorization header with an internal Functions-runtime token
        // before /api/* code runs. A custom header bypasses the rewrite.
        const res = await fetch('/api/policies', {
          headers: token ? { 'X-Auth-Token': token } : {},
        });
        if (!res.ok) {
          // /api/policies has its own JWT verifier (separate from PostgREST),
          // so a 401 here may be a config issue (audience, signing key,
          // issuer) rather than session expiry — bouncing the user through
          // Okta won't help and produces a sign-in loop. For 401/403 we
          // show user-friendly text; for unexpected statuses we surface
          // the API's detail to make config issues debuggable.
          const body = await res.json().catch(() => ({}));
          const detail = body.detail || body.error;
          let msg;
          if (res.status === 401) {
            msg = 'Your session has expired. Please sign in again.';
          } else if (res.status === 403) {
            msg = 'You do not have permission to view policy documents.';
          } else if (res.status >= 500) {
            msg = detail
              ? `The policy documents service is unavailable: ${detail}`
              : 'The policy documents service is unavailable. Try again in a moment.';
          } else {
            msg = detail
              ? `Couldn’t load policy documents (status ${res.status}): ${detail}`
              : `Couldn’t load policy documents (status ${res.status}).`;
          }
          throw new Error(msg);
        }
        const body = await res.json();
        if (active) setData(body);
      } catch (err) {
        if (active) setError(err.message || 'Couldn’t load policy documents.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const buckets = data?.buckets || [];

  return (
    <div style={{ background: B.black, minHeight: '100vh', color: B.textPrimary }}>
      <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/')} aria-label="Back to portal" style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
            <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />
            <span style={{ fontSize: 13, color: B.textSecondary, letterSpacing: '0.06em' }}>myhr</span>
          </button>
          <span style={{ width: 1, height: 16, background: B.border, margin: '0 4px' }} />
          <button onClick={() => navigate('/')} style={{ fontSize: 11, color: B.textSecondary, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.05em' }}>BACK TO PORTAL</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 12, color: B.textSecondary }}>policy documents</span>
          <button type="button" onClick={() => oktaAuth.signOut()} aria-label="Sign out" style={{ width: 32, height: 32, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: B.purple, cursor: 'pointer', padding: 0 }}>
            {initials(employee?.name) || 'ME'}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 14, textTransform: 'uppercase' }}>Policy documents</p>
        <p style={{ fontSize: 13, color: B.textSecondary, marginBottom: 24, lineHeight: 1.6 }}>
          Company documents live in SharePoint, organized into Policies, Benefits, and Employee Resources. Click any document to open it in your browser.
        </p>

        {loading && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 10, padding: '6px', minHeight: 180 }}>
            <p style={{ padding: 24, color: B.textSecondary, fontSize: 13, textAlign: 'center' }}>Loading…</p>
          </div>
        )}
        {error && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 10, padding: '6px', minHeight: 180 }}>
            <p style={{ padding: 24, color: '#c0392b', fontSize: 13, textAlign: 'center' }}>{error}</p>
          </div>
        )}
        {!loading && !error && buckets.length === 0 && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 10, padding: '6px', minHeight: 180 }}>
            <p style={{ padding: 24, color: B.textSecondary, fontSize: 13, textAlign: 'center' }}>No documents found.</p>
          </div>
        )}
        {!loading && !error && buckets.map((bucket, i) => (
          <BucketSection key={`${bucket.name}::${bucket.path}`} bucket={bucket} defaultExpanded={i === 0} />
        ))}

        {data?.fetched_at && (
          <p style={{ fontSize: 11, color: B.textMuted, marginTop: 16, textAlign: 'right' }}>
            Last fetched {new Date(data.fetched_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
